import { useCallback, useEffect, useState } from 'react';

import {
  getLoginSecretStorageUnlockPhase,
  isLoginSecretStorageUnlockActive,
} from 'contexts/MatrixContext/loginSecretStorageUnlock';
import useMatrixContext from 'contexts/MatrixContext/useMatrixContext';
import useMxCookies from 'modules/Authentication/hooks/useMxCookies';

import { CrossSigningStatusEnum } from './types';

const useHasVerifiedDevices = () => {
  const { matrixClient, matrixClientRef } = useMatrixContext({ requireDefinedMatrixClient: false });
  const { mxUserId, mxDeviceId, mxLoggedIn } = useMxCookies();
  const [devicesVerificationStatus, setDevicesVerificationStatus] = useState<CrossSigningStatusEnum>(
    CrossSigningStatusEnum.UNKNOWN
  );
  const [isLoading, setIsLoading] = useState(true);

  const checkForVerifiedDevices = useCallback(async () => {
    setIsLoading(true);
    const isLoginUnlock = isLoginSecretStorageUnlockActive();
    const client = matrixClient ?? (isLoginUnlock ? matrixClientRef.current : null);

    if (!isLoginUnlock && !mxLoggedIn) {
      setDevicesVerificationStatus(CrossSigningStatusEnum.NOT_VERIFIED);
      setIsLoading(false);
      return;
    }
    if (!client || !mxUserId || !mxDeviceId) {
      setDevicesVerificationStatus(CrossSigningStatusEnum.UNKNOWN);
      setIsLoading(false);
      return;
    }

    const crypto = client.getCrypto();
    if (!crypto) {
      setDevicesVerificationStatus(CrossSigningStatusEnum.UNKNOWN);
      setIsLoading(false);
      return;
    }

    // Before startClient runs (pre_sync phase) there is no sync loop, so device-list queries
    // and cross-signing signature checks are unreliable. Return NOT_VERIFIED immediately and
    // skip the network call — ChooseVerificationMethodModal will show the pre_sync bypass
    // button instead of the normal SAS button.
    //
    // After startClient (post_bootstrap phase) the sync has populated the local store with
    // public cross-signing keys, so getUserDeviceInfo + getDeviceVerificationStatus work
    // correctly. Fall through to the normal device check below.
    if (isLoginUnlock && getLoginSecretStorageUnlockPhase() === 'pre_sync') {
      setDevicesVerificationStatus(CrossSigningStatusEnum.NOT_VERIFIED);
      setIsLoading(false);
      return;
    }

    try {
      // Force refresh device list to get latest verification status
      const userDevices = (await crypto.getUserDeviceInfo([mxUserId], true)).get(mxUserId)?.values() ?? [];

      let foundVerified = false;
      for (const device of userDevices) {
        if (device.deviceId === mxDeviceId || !device.getIdentityKey()) continue;

        try {
          const verificationStatus = await crypto.getDeviceVerificationStatus(mxUserId, device.deviceId);
          if (verificationStatus?.signedByOwner || verificationStatus?.crossSigningVerified) {
            foundVerified = true;
            break;
          }
        } catch (error) {
          console.warn(`Failed to check verification status for device ${device.deviceId}:`, error);
          continue;
        }
      }

      setDevicesVerificationStatus(
        foundVerified ? CrossSigningStatusEnum.VERIFIED : CrossSigningStatusEnum.NOT_VERIFIED
      );
    } catch (error) {
      console.error('Failed to check for verified devices:', error);
      setDevicesVerificationStatus(CrossSigningStatusEnum.NOT_VERIFIED);
    } finally {
      setIsLoading(false);
    }
  }, [matrixClient, matrixClientRef, mxUserId, mxDeviceId, mxLoggedIn]);

  useEffect(() => {
    checkForVerifiedDevices();
  }, [checkForVerifiedDevices]);

  return { devicesVerificationStatus, checkForVerifiedDevices, isLoading };
};

export default useHasVerifiedDevices;
